<script setup lang="ts">
import { ref, computed, reactive, watch, type PropType } from 'vue'
import useVuelidate from '@vuelidate/core'
import {
	required,
	minLength,
	maxLength,
	sameAs,
	helpers,
} from '@vuelidate/validators'
import DialogRevamp from '@/components/DialogRevamp.vue'
import InputFieldFloatingWithMessage from '@/components/ui/input/InputFieldFloatingWithMessage.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import Newsletter from '@/components/auth/Newsletter.vue'
import BaseSelectDropdown, {
	type DropdownOption,
} from '@/components/ui/dropdown/BaseSelectDropdown.vue'
import { getCurrentGaPageName } from '@/lib/utils'
import { GaGeneralPageName } from '@/assets/ts/customEvent'
import { isGlobalSite } from '@/lib/checkService'

enum TypeSignup {
	Normal = 'normal',
	Social = 'social',
}

const props = defineProps({
	type: {
		type: String as PropType<`${TypeSignup}`>,
		default: TypeSignup.Normal,
	},
	errorApiMessage: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['hide', 'backStep', 'submit', 'clickNoFullName'])
const textContent: any = injectStrict('textContent')
const isInGlobalSite = isGlobalSite()
const { isShowDialog } = useAuthDialog(hideDialogDirectly)

const isLoading = ref(false)

const formState = reactive<{
	fullName: string
	password: string
	confirmPassword: string
	livingCountry: DropdownOption | null
	invite: string
	newsletter: boolean
}>({
	fullName: '',
	password: '',
	confirmPassword: '',
	livingCountry: null,
	invite: window.BUYANDSHIP.settings.member_invite || '',
	newsletter: true,
})

const validateNameRules = {
	fullName: {
		required,
		maxLength: maxLength(20),
		alphaAndSpacesOnly: helpers.regex(/^[a-zA-Z\s]*$/),
	},
}

const isTypeSocial = computed(() => props.type === TypeSignup.Social)
const isHideInviteCode = computed(
	() => getCurrentGaPageName() === GaGeneralPageName.InvitePage,
)
const livingCountryOptions = computed(() => {
	const countries = window.BUYANDSHIP.settings.all_countries?.global?.countries

	if (!countries) {
		return []
	}

	return Object.entries(countries)
		.map(([countryCode, country]) => ({
			label: country.name,
			value: countryCode,
		}))
		.sort((a, b) => a.label.localeCompare(b.label))
})

const rules = () => {
	const baseValidations = {
		...validateNameRules,
		livingCountry: isInGlobalSite ? { required } : {},
	}

	if (isTypeSocial.value) {
		return baseValidations
	}
	return {
		...baseValidations,
		password: {
			required,
			minLength: minLength(6),
		},
		confirmPassword: {
			required,
			minLength: minLength(6),
			sameAsPassword: sameAs(computed(() => formState.password)),
		},
	}
}
const $v = useVuelidate(rules(), formState)

const canCloseDialogByOverlay = computed(() => !isTypeSocial.value)
const currentTitle = computed(() => {
	if (isTypeSocial.value) {
		return textContent.promo_code_title
	}
	return textContent.personal_details_title
})
const passwordErrorMessage = computed(() => {
	if ($v.value.password.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.password.minLength.$invalid) {
		return textContent.input_error_password_min_length
	}
	return ''
})

const passwordConfirmErrorMessage = computed(() => {
	if ($v.value.confirmPassword.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.confirmPassword.minLength.$invalid) {
		return textContent.input_error_password_min_length
	}
	if ($v.value.confirmPassword.sameAsPassword.$invalid) {
		return textContent.input_error_password_same
	}
	return ''
})

const fullNameErrorMessage = computed(() => {
	if ($v.value.fullName.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.fullName.maxLength.$invalid) {
		return textContent.input_error_name_limit
	}
	if ($v.value.fullName.alphaAndSpacesOnly.$invalid) {
		return textContent.input_error_name_limit
	}
	return ''
})

const livingCountryErrorMessage = computed(() => {
	if ($v.value.livingCountry.required.$invalid) {
		return textContent.input_error_general_required
	}
	return ''
})
const isErrorApiMessageExist = computed(() => props.errorApiMessage !== '')

function onOverlayClick() {
	if (isTypeSocial.value) {
		emit('clickNoFullName')
	} else {
		backStep()
	}
}

function backStep() {
	emit('backStep')
}

function submit() {
	$v.value.$touch()
	if ($v.value.$invalid) {
		return
	}
	isLoading.value = true
	emit('submit', {
		fullName: formState.fullName,
		password: formState.password,
		invite: formState.invite,
		newsletter: formState.newsletter,
		livingCountry: formState.livingCountry?.value ?? '',
	})
}

function hideDialogDirectly() {
	emit('hide')
}

function onUpdateNewsletter(value: boolean) {
	formState.newsletter = value
}

function onLivingCountryBlur() {
	$v.value.livingCountry.$touch()
}

watch(
	() => isErrorApiMessageExist.value,
	value => {
		if (value) {
			isLoading.value = false
		}
	},
)
</script>

<template>
	<DialogRevamp
		class="bs-auth-signup"
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isCustomOverlayFunction="true"
		:isSecondLayer="true"
		:isShowDefaultClose="false"
		:size="'sm'"
		:canCloseDialogByOverlay="canCloseDialogByOverlay"
		@overlayClick="onOverlayClick"
	>
		<template #header>
			<div class="bs-auth-back-header">
				<button
					v-if="!isTypeSocial"
					class="bs-icon bs-auth-back-header__back"
					@click="backStep"
				>
					<BaseIcon :name="'back'" class="h-16px w-16px fill-n-950" />
				</button>
				<h5 class="bs-auth__title">
					{{ currentTitle }}
				</h5>
			</div>
		</template>
		<template #content>
			<div class="bs-auth-signup__content">
				<BaseAttentionBox
					v-if="isErrorApiMessageExist"
					class="bs-auth__attention-box"
					:icon="'error'"
					:background="'error'"
				>
					<template #description>
						{{ errorApiMessage }}
					</template>
				</BaseAttentionBox>
				<InputFieldFloatingWithMessage
					class="bs-auth-signup__content__full-name"
					v-model="$v.fullName.$model"
					type="text"
					:disabled="isLoading"
					:label="textContent.input_label_name"
					:isError="$v.fullName.$error"
					:errorMessage="fullNameErrorMessage"
					autoComplete="off"
				></InputFieldFloatingWithMessage>

				<BaseSelectDropdown
					v-if="isInGlobalSite"
					class="bs-auth-signup__content__country"
					v-model="$v.livingCountry.$model"
					:options="livingCountryOptions"
					:maxDisplayItems="5"
					:isDisabled="isLoading"
					:label="textContent.global_site_living_country_label"
					:placeholder="textContent.global_site_select_living_country_text"
					:isError="$v.livingCountry.$error"
					:errorMessage="livingCountryErrorMessage"
					@blur-without-selection="onLivingCountryBlur"
				></BaseSelectDropdown>

				<p class="bs-auth-signup__content__description" v-if="!isTypeSocial">
					{{ textContent.personal_details_description }}
				</p>

				<InputFieldFloatingWithMessage
					class="bs-auth-signup__content__password"
					v-if="!isTypeSocial"
					v-model="$v.password.$model"
					type="password"
					:disabled="isLoading"
					:label="textContent.input_label_password_reset"
					:isError="$v.password.$error"
					:errorMessage="passwordErrorMessage"
					autoComplete="new-password"
				></InputFieldFloatingWithMessage>

				<InputFieldFloatingWithMessage
					class="bs-auth-signup__content__confirm-password"
					v-if="!isTypeSocial"
					v-model="$v.confirmPassword.$model"
					type="password"
					:disabled="isLoading"
					:label="textContent.input_label_password_confirm_reset"
					:isError="$v.confirmPassword.$error"
					:errorMessage="passwordConfirmErrorMessage"
				></InputFieldFloatingWithMessage>

				<template v-if="!isHideInviteCode">
					<p class="bs-auth-signup__content__description">
						{{ textContent.promo_code_description }}
					</p>

					<InputFieldFloatingWithMessage
						class="bs-auth-signup__content__invite"
						v-model="formState.invite"
						type="text"
						:disabled="isLoading"
						:isError="false"
						:label="textContent.input_label_promo_code"
					></InputFieldFloatingWithMessage>
				</template>

				<Newsletter
					:modelValue="formState.newsletter"
					:paragraph="textContent.newsletter_paragraph"
					@update:modelValue="onUpdateNewsletter"
				/>
			</div>
		</template>
		<template #footer>
			<BaseButton
				:size="'large'"
				:isFull="true"
				:isLoading="isLoading"
				@click="submit"
			>
				{{ textContent.button_confirm }}
			</BaseButton>
		</template>
	</DialogRevamp>
</template>
